import { ImpactStyle } from "@capacitor/haptics";
import { Listbox } from "@headlessui/react";
import React from "react";
import {
  matchPath,
  resolvePath,
  useLocation,
  useNavigate,
  useResolvedPath,
} from "react-router-dom";
import tw from "twin.macro";
import { hapticsImpact } from "~/config/capacitor/haptics/hook";
import { styled } from "~/config/stitches";
import { createComponentHook } from "~/types";
import { ButtonArrow } from "./components/ButtonArrow";
import type {
  NavOptionItemProps,
  NavOptionProps,
} from "./components/NavOption";
import { NavOption } from "./components/NavOption";
import { OptionContent } from "./components/OptionContent";
import { SelectButton } from "./components/SelectButton";
import { SelectOptions } from "./components/SelectOptions";

type NavSelectProps = {
  options: Array<NavOptionItemProps>;
  width?: "auto" | "full";
  rounded?: boolean;
  centered?: boolean;
  value?: NavOptionProps;
};

const useNavSelect = createComponentHook((props: NavSelectProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useResolvedPath("");

  const [selectedOption, setSelectedOption] = React.useState<
    NavOptionProps | undefined
  >(
    () =>
      props.value ??
      props.options.find((option) => {
        const resolvedPath = resolvePath(option.to, pathname);
        return (
          matchPath(`${resolvedPath.pathname}/*`, location.pathname) !== null
        );
      })
  );

  React.useLayoutEffect(() => {
    // If controlled (value prop provided), use that value
    if (props.value) {
      setSelectedOption(props.value);
      return;
    }

    // Otherwise, use URL-based selection
    const selected = props.options.find((option) => {
      const resolvedPath = resolvePath(option.to, pathname);
      return (
        matchPath(`${resolvedPath.pathname}/*`, location.pathname) !== null
      );
    });
    // Make sure a new item is selected before re-rendering to avoid component blinking
    if (selected) {
      setSelectedOption(selected);
    }
  }, [props.options, props.value, location.pathname, pathname]);

  const handleChange = (option: NavOptionProps): void => {
    setSelectedOption(option);
    navigate(option.to);
  };

  const handleHaptic = async () => {
    await hapticsImpact(ImpactStyle.Light);
  };

  return {
    actions: { handleChange, handleHaptic },
    state: {
      selectedOption,
    },
  };
});

const SelectWrapper = styled("div", {
  ...tw`relative max-w-full`,

  defaultVariants: {
    width: "full",
  },

  variants: {
    width: {
      auto: tw`w-auto`,
      full: tw`w-full`,
    },
  },
});

function NavSelect(props: NavSelectProps) {
  const { actions, state } = useNavSelect(props);

  const NavSelectOptions = () => (
    <SelectOptions rounded={props.rounded}>
      {props.options.map((option) => (
        <NavOption key={option.to} {...option} centered={props.centered} />
      ))}
    </SelectOptions>
  );

  return (
    <Listbox value={state.selectedOption} onChange={actions.handleChange}>
      <SelectWrapper width={props.width} onClick={actions.handleHaptic}>
        <SelectButton rounded={props.rounded}>
          {state.selectedOption && (
            <OptionContent
              {...state.selectedOption}
              centered={props.centered}
            />
          )}
          <ButtonArrow />
        </SelectButton>
        <NavSelectOptions />
      </SelectWrapper>
    </Listbox>
  );
}

export { NavSelect };
